import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem, usePage } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { documents } from "@/lib/agents/search/documents"
import { app } from "@/lib/app"
import { zodResolver } from "@hookform/resolvers/zod"
import { BookCopy } from "lucide-react"
import { useForm } from "react-hook-form"
import { estimateTokenCount } from "tokenx"
import { z } from "zod"

type Vector = Table<"vectors">

export const supacrud = {
  title: "Vector",
  description: "Manage your organization's Documents.",
  route: "/docu/supa/:id",
  icon: BookCopy,
  component,
  group: "docs",
  resource: "collections",
  action: "read",
} satisfies PageConfig

const FormSchema = z.object({
  name: z.string().optional().nullable(),
  content: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
})

type FormData = z.infer<typeof FormSchema>

export function component() {
  const { data, loading, submit, toastSuccess, navigate, toast } = useDatabaseItem("vectors")
  const { team } = usePage()


  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data,
    values: data,
  })

  async function onSubmit(data: Vector) {
    console.log("Submit: ", data)
    const apikeys = await app.fetchAPIkeys(team?.id)
    if (!apikeys || !apikeys.openai) {
      toast.error("OpenAI API key not found")
      return
    }
    const vector = await documents.createEmbedding(data.content, apikeys.openai)
    data.vector = vector as any
    data.tokens = estimateTokenCount(data.content)
    await submit(data)
    toastSuccess()
    navigate("/docu/supa")
  }

  if (loading) return <CRUDLoader />

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="vectors" onSuccess={"/docu/supa"} />
          </PageTitle>
          <div className="rgrid">
            <div className="row">
              <div className="rc-12">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name {data.isChunk ? "(Chunk)" : ""}</FormLabel>
                    <FormControl><Input {...field} value={field.value || ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
            <div className="row">
              <div className="rc-12">
                <FormField control={form.control} name="locale" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Locale</FormLabel>
                    <FormControl><Input {...field} value={field.value || ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div className="row">
              <div className="rc-12">
                <FormField control={form.control} name="content" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl><Textarea {...field} value={field.value || ""} rows={20} className="font-mono text-sm" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>
}
